import { CellSource, CellType, ColumnSource, Options, RequestFilters, RowSource } from "@omegagrid/grid";
import { ReportContext } from "@reporting/rp-types/analytics";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { ReportBroker } from "../../model";
import { msg } from "@omegagrid/localize";
import { dates } from "@omegagrid/core";
import { TransactionTotalItem } from "../../types";
import { TransactionResult } from "../../model/transactionResult";
import rp, { BaseGridAdapter } from "@reporting/rp-app-host";
import { ColumnSettings } from "./transactions";
import { getModuleKey } from "../../common";

const DEFAULT_COLUMN_WIDTH = 120;
const PAGE_SIZE = 500;

const CELL_TYPES = new Set<string>(['n', 't', 'd', 'tm', 'dt', 'b', 's', 'm', 'g', 'a', 'h']);

/** columns outside the server definitions carry raw SQL type names */
const SQL_CELL_TYPES: Record<string, CellType> = {
	float: 'n', real: 'n', int: 'n', bigint: 'n', smallint: 'n', tinyint: 'n', decimal: 'n', numeric: 'n', money: 'n',
	date: 'd', datetime: 'dt', datetime2: 'dt', smalldatetime: 'dt', time: 'tm',
	bit: 'b',
};

function toCellType(type: string): CellType {
	if (!type) return 'g';
	if (CELL_TYPES.has(type)) return type as CellType;
	return SQL_CELL_TYPES[type.toLowerCase()] ?? 'g';
}

/** the grid's default date format has no time part, datetime columns need it explicitly */
function defaultFormat(type: CellType): string | undefined {
	return type === 'dt' ? dates.getDateFormat({ withTime: true }) : undefined;
}

export class TransactionAdapter extends BaseGridAdapter {

	private _rows: RowSource[] | null = null;
	private _columns: ColumnSource[] | null = null;
	private _totals: TransactionTotalItem[] | null = null;
	private _firstLoad = true;

	public pageSize = PAGE_SIZE;

	constructor(
		readonly reportContext: ReportContext,
		readonly reportBroker: ReportBroker
	) { super() }

	get columnSettingsKey() {
		return `analytics.${getModuleKey(this.reportContext.module as ModuleCode)}.columns`;
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		await this.getRows({offset: 0});

		const settings = new Map<string, ColumnSettings & {ord?: number}>();

		(rp.settings.get(this.columnSettingsKey) ?? []).forEach((setting: ColumnSettings & {ord?: number}, i: number) => {
			const s = Object.assign({}, setting, {ord: i});
			settings.set(setting.column, setting);
		});

		this._columns.sort((a, b) => {
			const aSetting = settings.get(a.name);
			const bSetting = settings.get(b.name);
			if (aSetting && bSetting) return aSetting.ord - bSetting.ord;
			return 0;
		});

		this._columns.forEach(col => {
			const setting = settings.get(col.name);
			if (setting) {
				col.hidden = !!setting.hidden;
			}
		});

		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		if (!this._firstLoad && !filters?.offset && !filters?.filter && !filters?.order) return this._rows;
		this._firstLoad = false;
		let result: TransactionResult;

		const filterColNameReplacements: Record<string, string> = {};
		if (this._columns) {
			this._columns.forEach(col => filterColNameReplacements[col.data.toString()] = col.name);
		}

		try {
			result = await this.reportBroker.getTransactionData(this.reportContext, this.createRequestParams(filters, {
				count: this.pageSize
			}, filterColNameReplacements));
		} catch (error) {
			// TODO: move error handling out of adapter
			rp.log.error(error);
			rp.host.showAlert({ type: 'danger', content: msg('Error fetching transaction data') });
			return this._rows;
		}

		this._totals = result.totals;
		this._columns = result.columns.map((col, index) => {
			const t = toCellType(col.type);
			return {
				name: col.name,
				data: index,
				sorting: true,
				hidden: !!col.hidden,
				header: msg(col.header),
				z: col.format || defaultFormat(t),
				w: col.width > 0 ? col.width : DEFAULT_COLUMN_WIDTH,
				t,
			};
		});

		this._rows = result.data.map((data, rowIndex) => ({ data }));
		return this._rows;
	}

	async getCells(filters?: RequestFilters): Promise<CellSource[]> {
		return [];
	}

	getOptions(): Promise<Partial<Options>> {
		return Promise.resolve({
			hideOuterBorder: true,
			pageSize: this.pageSize,
			rows: 100,
			columns: 20,
			autofill: false,
			filters: {
				enabled: true,
			},
			sorting: {
				enabled: true,
			},
			rowNumbers: true,
			colLetters: false,
		});
	}

	updateColumns(columns: ColumnSettings[]) {
		rp.settings.set(this.columnSettingsKey, columns, 'user_accounting', rp.accountingProvider.a);
	}

	async getTotals(): Promise<TransactionTotalItem[]> {
		await this.getRows();
		return this._totals || [];
	}

}
